import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MapPin, Phone, Mail, Facebook, Linkedin, Youtube, Instagram, Twitter } from "lucide-react";

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_jci-content-platform/artifacts/mv5wtmf7_Asset%203.png";
const RIPPLE_HALF = "https://customer-assets.emergentagent.com/job_jci-content-platform/artifacts/nnk3iw5x_Ripples_Half.png";

const ICON_MAP = { Facebook, Linkedin, Youtube, Instagram, Twitter };

const QUICK_LINKS = [
  { label: "About JCI Bangladesh", path: "/page/about-jci-bangladesh" },
  { label: "National Governing Board", path: "/team/governing-board" },
  { label: "Activities Calendar", path: "/page/activities-calendar" },
  { label: "Hall of Fame", path: "/page/awards-showcase" },
  { label: "Media Gallery", path: "/media-gallery" },
  { label: "Store", path: "/store" },
];

const RESOURCES = [
  { label: "Downloads", path: "/page/downloads" },
  { label: "Brand Guidelines", path: "/page/brand-guidelines" },
  { label: "Publications", path: "/page/publications" },
  { label: "News & Press", path: "/news-press" },
  { label: "Content Hub", path: "/content-hub" },
];

export default function Footer() {
  const [socials, setSocials] = useState([]);

  useEffect(() => {
    api.get("/content/social_settings").then(res => {
      setSocials(res.data);
    }).catch(() => {});
  }, []);

  const getSocialUrl = (platform) => {
    const s = socials.find(x => x.platform?.toLowerCase() === platform.toLowerCase());
    return s?.url || "#";
  };

  const socialLinks = [
    { platform: "Facebook", icon: Facebook },
    { platform: "Linkedin", icon: Linkedin },
    { platform: "Youtube", icon: Youtube },
    { platform: "Instagram", icon: Instagram },
  ];

  return (
    <footer data-testid="main-footer" className="bg-jci-black text-white relative overflow-hidden">
      {/* Ripple decoration - static, fill height, anchored right */}
      <img
        src={RIPPLE_HALF}
        alt=""
        className="absolute right-0 top-0 h-full w-auto opacity-[0.04] pointer-events-none z-0"
        style={{ objectFit: "cover", objectPosition: "right" }}
      />

      {/* Top border accent */}
      <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img src={LOGO_DARK} alt="JCI Bangladesh" className="h-12 mb-6" />
            <p className="text-white/50 text-sm leading-relaxed mb-6">
              JCI Bangladesh empowers young leaders to create positive change through leadership development, community service, and international cooperation.
            </p>
            <div className="flex gap-3">
              {socialLinks.map(({ platform, icon: Icon }, i) => (
                <a key={i} href={getSocialUrl(platform)} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 hover:bg-jci-blue/20 flex items-center justify-center transition-colors" data-testid={`social-icon-${platform.toLowerCase()}`}>
                  <Icon className="w-4 h-4 text-white/60 hover:text-jci-blue" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-jci-blue mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map((link, i) => (
                <li key={i}>
                  <Link to={link.path} className="text-sm text-white/50 hover:text-jci-teal transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-jci-blue mb-6">Resources</h4>
            <ul className="space-y-3">
              {RESOURCES.map((link, i) => (
                <li key={i}>
                  <Link to={link.path} className="text-sm text-white/50 hover:text-jci-teal transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-jci-blue mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex gap-3 items-start">
                <MapPin className="w-4 h-4 text-jci-teal mt-0.5 flex-shrink-0" />
                <span className="text-sm text-white/50">House 11A, Road 113, Gulshan, Dhaka</span>
              </li>
              <li className="flex gap-3 items-start">
                <Phone className="w-4 h-4 text-jci-teal mt-0.5 flex-shrink-0" />
                <span className="text-sm text-white/50">+8801843632811</span>
              </li>
              <li className="flex gap-3 items-start">
                <Mail className="w-4 h-4 text-jci-teal mt-0.5 flex-shrink-0" />
                <span className="text-sm text-white/50">bangladesh@jci.cc</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-5 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-white/30">&copy; {new Date().getFullYear()} JCI Bangladesh. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/page/privacy-policy" className="text-xs text-white/30 hover:text-white/60 transition-colors">Privacy Policy</Link>
            <Link to="/page/terms" className="text-xs text-white/30 hover:text-white/60 transition-colors">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
